import React, { useCallback, useRef, useState } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay';

import { cssMap, cx } from '@atlaskit/css';
import { Box } from '@atlaskit/primitives/compiled/box';
import { token } from '@atlaskit/tokens';

import { AvatarRelay, type AvatarRelayProps } from '@atlassian/avatar-relay/avatar-relay';

import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { GenericErrorBoundary } from '@confluence/error-boundary/entry-points/GenericErrorBoundary.ts';
import { getMonitoringClient } from '@confluence/monitoring/entry-points/getMonitoringClient.ts';
import { RelaySuspense } from '@confluence/query-preloader-tools/entry-points/RelaySuspense.ts';

import type { AgentAvatarSize } from '../shared/AgentPresenceOutline.tsx';
import { AgentPresenceAvatarFrame } from './AgentPresenceAvatarFrame.tsx';

import type { AgentIdentityAvatarQuery } from './__generated__/AgentIdentityAvatarQuery.graphql.ts';

const styles = cssMap({
	picture: { display: 'inline-flex', flexShrink: 0 },
	hidden: { visibility: 'hidden' },
});

type AgentIdentityAvatarProps = {
	accountId: string;
	name: string;
	size: AgentAvatarSize;
	showBorder?: boolean;
	stackIndex?: number;
	isOverlapped?: boolean;
};

type IdentityPictureProps = Pick<AgentIdentityAvatarProps, 'name' | 'size' | 'showBorder'>;

function IdentityAvatar({ accountId, ...props }: IdentityPictureProps & { accountId: string }) {
	const data = useLazyLoadQuery<AgentIdentityAvatarQuery>(
		graphql`
			query AgentIdentityAvatarQuery($accountId: ID!) {
				user(accountId: $accountId) {
					picture
					...avatarRelay_user
				}
			}
		`,
		{ accountId },
		{ fetchPolicy: 'store-or-network' },
	);

	return (
		<IdentityPicture
			key={data.user?.picture}
			{...props}
			hasPicture={Boolean(data.user?.picture?.trim())}
			userFragment={data.user}
		/>
	);
}

function IdentityPicture({
	userFragment,
	hasPicture,
	name,
	size,
	showBorder,
}: IdentityPictureProps & {
	userFragment: AvatarRelayProps['userFragment'];
	hasPicture: boolean;
}) {
	const [isImageLoading, setIsImageLoading] = useState(hasPicture);
	const hasReportedImageFailure = useRef(false);
	const handleLoad = useCallback(() => setIsImageLoading(false), []);
	const handleError = useCallback(() => {
		setIsImageLoading(false);
		if (!hasReportedImageFailure.current) {
			hasReportedImageFailure.current = true;
			getMonitoringClient().incrementCounter('agentPresence.identityAvatarFallback', [
				'reason:imageLoadFailed',
				`attribution:${Attribution.COLLABORATION_PRESENCE}`,
			]);
		}
	}, []);
	return (
		<Box
			xcss={cx(styles.picture, isImageLoading && styles.hidden)}
			aria-hidden={isImageLoading || undefined}
			onLoadCapture={handleLoad}
			onErrorCapture={handleError}
			testId="agent-presence-identity-picture"
		>
			<AvatarRelay
				userFragment={userFragment}
				name={name}
				label={name}
				size={size}
				borderColor={showBorder ? token('elevation.surface') : 'transparent'}
				UNSAFE_isUpdatedGeometry
				imgLoading="eager"
				testId="agent-presence-identity-avatar"
			/>
		</Box>
	);
}

export function AgentIdentityAvatar({
	stackIndex,
	isOverlapped,
	...props
}: AgentIdentityAvatarProps): React.JSX.Element {
	return (
		<AgentPresenceAvatarFrame size={props.size} stackIndex={stackIndex} isOverlapped={isOverlapped}>
			<GenericErrorBoundary
				key={props.accountId}
				attribution={Attribution.COLLABORATION_PRESENCE}
				renderOnError={() => null}
			>
				<RelaySuspense fallback={null} interactionName="AgentIdentityAvatarQuery">
					<IdentityAvatar {...props} />
				</RelaySuspense>
			</GenericErrorBoundary>
		</AgentPresenceAvatarFrame>
	);
}
